import { defineReceiver, defineCommand } from '@drapejs/invoker';
import { request, query, sales } from '@distancify/drapejs-litium';
import { gql } from 'graphql-request';

export const commands = {
  addToCart: defineCommand<{
    url: string;
    articleNumber: string;
    quantity: number;
  }>('addToCart'),  
  setRowQuantity: defineCommand<{
    url: string;
    rowId: string;
    syncDelivery: boolean;
    quantity: number;
  }>('setRowQuantity'),
  changeDeliveryPreference: defineCommand<{
    url: string;
    pickedShippingDate: Date;
    deliveryPreference: string;
  }>('changeDeliveryPreference'),
  placeWholesaleOrder: defineCommand<{
    url: string;
    input: {
      projectReference: string;
      comments: string;
      subcribe: string;
    };
  }>('placeWholesaleOrder'),
  setLogTradeDeliveryOption: defineCommand<{
    optionId: string;
    url: string;
  }>('setLogTradeDeliveryOption'),
};

export const receivers = {
  addToCart: defineReceiver(
    commands.addToCart,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation addToCart(
              $url: String!
              $articleNumber: String!
              $quantity: Decimal!
            ) {
              session(url: $url) {
                addToCart(
                  articleNumber: $articleNumber
                  quantity: $quantity
                ) {
                  error
                  row {
                    ...RowFields
                  }
                  cart {
                    ...CartFields
                  }
                }
              }
            }
          `,
          ...sales.withCartFields()
        ),
        {
          url: command.url,
          articleNumber: command.articleNumber,
          quantity: command.quantity,
        }
      );

      await this.cache.setItem(
        '__cart',
        result.session.addToCart.cart
      );

      return result.session.addToCart;
    },
    'litium'
  ),  
  setRowQuantity: defineReceiver(
    commands.setRowQuantity,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation setCartRowQuantity(
              $url: String!
              $rowId: String!
              $quantity: Decimal!
              $syncDelivery: Boolean
            ) {
              session(url: $url) {
                setCartRowQuantity(
                  rowId: $rowId
                  quantity: $quantity
                  syncDelivery: $syncDelivery
                ) {
                  error
                  row {
                    ...RowFields
                  }
                  cart {
                    ...CartFields
                  }
                }
              }
            }
          `,
          ...sales.withCartFields()
        ),
        {
          url: command.url,
          rowId: command.rowId,
          quantity: command.quantity,
          syncDelivery: command.syncDelivery || false,
        }
      );

      await this.cache.setItem(
        '__cart',
        result.session.setCartRowQuantity.cart
      );

      return result.session.setCartRowQuantity;
    },
    'litium'
  ),
  changeDeliveryPreference: defineReceiver(
    commands.changeDeliveryPreference,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation changeDeliveryPreference(
              $url: String!
              $deliveryPreference: String!
              $pickedShippingDate: DateTime
            ) {
              session(url: $url) {
                changeDeliveryPreference(
                  deliveryPreference: $deliveryPreference
                  pickedShippingDate: $pickedShippingDate
                ) {
                  error
                  cart {
                    ...CartFields
                  }
                }
              }
            }
          `,
          ...sales.withCartFields()
        ),
        {
          url: command.url,
          deliveryPreference: command.deliveryPreference,
          pickedShippingDate: command.pickedShippingDate || new Date(),
        }
      );

      await this.cache.setItem(
        '__cart',
        result.session.changeDeliveryPreference.cart
      );

      return result.session.changeDeliveryPreference;
    },
    'litium'
  ),
  placeWholesaleOrder: defineReceiver(
    commands.placeWholesaleOrder,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation placeWholesaleOrder(
              $url: String
              $input: PlaceWholesaleOrderInput!
            ) {
              session(url: $url) {
                placeWholesaleOrder(input: $input) {
                  error
                  orderId
                  cart {
                    ...CartFields
                  }
                }
              }
            }
          `,
          ...sales.withCartFields()
        ),
        {
          url: command.url,
          input: command.input,
        }
      );

      const { cart, error } = result.session.placeWholesaleOrder;
      if (error !== 'NONE') {
        await this.cache.setItem('__cart', cart);
      }

      return result.session.placeWholesaleOrder;
    },
    'litium'
  ),
  setLogTradeDeliveryOption: defineReceiver(
    commands.setLogTradeDeliveryOption,
    async function (command, data) {
      const result = await request(
        this.cache,
        query(
          gql`
            mutation setLogTradeDeliveryOption(
              $url: String
              $optionId: String!
            ) {
              session(url: $url) {
                setLogTradeDeliveryOption(optionId: $optionId) {
                  error
                  cart {
                    ...CartFields
                  }
                }
              }
            }
          `,
          ...sales.withCartFields()
        ),
        {
          url: command.url,
          optionId: command.optionId,
        }
      );

      await this.cache.setItem(
        '__cart',
        result.session.setLogTradeDeliveryOption.cart
      );

      return result.session.setLogTradeDeliveryOption;
    },
    'litium'
  ),
};
